import React, { createContext, useContext, useState, useCallback } from 'react';
import { 
  Toast, 
  ToastClose, 
  ToastDescription, 
  ToastProvider, 
  ToastTitle, 
  ToastViewport 
} from '../components/ui/toast';
import { 
  CheckCircle, 
  AlertCircle, 
  AlertTriangle, 
  Info, 
  FileCheck,
  Upload,
  Download,
  Trash2,
  Eye,
  Mail,
  Settings
} from 'lucide-react';

const SnackbarContext = createContext();

// Enhanced icon mapping for different types of messages
const getToastIcon = (severity, type) => {
  const iconMap = {
    success: {
      upload: <Upload className="w-5 h-5" />,
      download: <Download className="w-5 h-5" />,
      delete: <Trash2 className="w-5 h-5" />,
      processing: <Settings className="w-5 h-5 animate-spin" />,
      email: <Mail className="w-5 h-5" />,
      file: <FileCheck className="w-5 h-5" />,
      default: <CheckCircle className="w-5 h-5" />
    },
    error: {
      upload: <Upload className="w-5 h-5" />,
      download: <Download className="w-5 h-5" />,
      delete: <Trash2 className="w-5 h-5" />,
      processing: <Settings className="w-5 h-5" />,
      email: <Mail className="w-5 h-5" />,
      file: <Eye className="w-5 h-5" />,
      default: <AlertCircle className="w-5 h-5" />
    },
    warning: {
      default: <AlertTriangle className="w-5 h-5" />
    },
    info: {
      default: <Info className="w-5 h-5" />
    }
  };
  
  return iconMap[severity]?.[type] || iconMap[severity]?.default || iconMap.info.default;
};

// Enhanced title generation
const getToastTitle = (severity, type) => {
  const titleMap = {
    success: {
      upload: 'Upload Complete',
      download: 'Download Complete', 
      delete: 'File Deleted',
      processing: 'Processing Complete',
      email: 'Email Sent',
      file: 'File Ready',
      default: 'Success'
    },
    error: {
      upload: 'Upload Failed',
      download: 'Download Failed',
      delete: 'Delete Failed',
      processing: 'Processing Failed',
      email: 'Email Failed',
      file: 'Preview Failed',
      default: 'Error'
    },
    warning: {
      default: 'Warning'
    },
    info: {
      default: 'Information'
    }
  };
  
  return titleMap[severity]?.[type] || titleMap[severity]?.default || titleMap.info.default;
};

export const SnackbarProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((toast) => {
    const id = Date.now() + Math.random();
    const newToast = { ...toast, id };
    
    setToasts((prev) => [...prev, newToast]);
    
    // Auto-hide toast after duration
    if (toast.duration !== 0) {
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, toast.duration || 5000);
    }
    
    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showSnackbar = useCallback(({ 
    message, 
    severity = 'success', 
    duration = 5000,
    type = 'default',
    title,
    action 
  }) => {
    const toastTitle = title || getToastTitle(severity, type);
    const icon = getToastIcon(severity, type);
    
    // Map severity to variant
    const variantMap = {
      success: 'success',
      error: 'destructive', 
      warning: 'warning',
      info: 'info'
    };
    
    return addToast({
      title: toastTitle,
      description: message,
      variant: variantMap[severity] || 'default',
      duration,
      icon,
      action
    });
  }, [addToast]);

  const hideSnackbar = useCallback((id) => {
    if (id) {
      removeToast(id);
    } else {
      // Hide all toasts if no ID provided
      setToasts([]);
    }
  }, [removeToast]);

  return (
    <SnackbarContext.Provider value={{ showSnackbar, hideSnackbar }}>
      {children}
      <ToastProvider>
        {toasts.map((toast) => (
          <Toast key={toast.id} variant={toast.variant} className="group">
            <div className="flex items-start gap-3 w-full">
              <div className="flex-shrink-0 mt-0.5 opacity-90">
                {toast.icon}
              </div>
              <div className="flex-1 min-w-0">
                <ToastTitle className="font-semibold text-sm mb-1">
                  {toast.title}
                </ToastTitle>
                <ToastDescription className="text-sm leading-relaxed opacity-90">
                  {toast.description}
                </ToastDescription>
              </div>
            </div>
            {toast.action && (
              <div className="ml-auto flex-shrink-0">
                {toast.action}
              </div>
            )}
            <ToastClose onClick={() => removeToast(toast.id)} />
          </Toast>
        ))}
        <ToastViewport />
      </ToastProvider>
    </SnackbarContext.Provider>
  );
};

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
};
